import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { getMochiMeter } from "@/lib/mochiMeter";
import { pickHungerDropPhrase } from "@/lib/hungerPhrases";

// Watches the Hunger Meter level (derived from `settings.meter_value`) and
// fires a single soft Mochi-voice toast whenever the level DROPS by at
// least one stage. Rises (manual feed / task-completion auto-feed) are
// silent. First observation is also silent so we don't fire on app boot
// just because the user's level happened to be lower than "max."
//
// Implementation notes:
//   - `lastLevelRef` lives outside the effect dep array on purpose — we
//     only care about the most recent value, never a stale closure.
//   - `meter_last_update_at` is included so the hook reacts to the timestamp
//     update too (in some flows the value can briefly stay the same while
//     decay is recomputed elsewhere).
//   - We deduplicate on level (an integer) rather than on the float
//     `meter_value` to avoid firing on sub-stage decay ticks.
export function useHungerDrop({ settings }) {
    const lastLevelRef = useRef(null);
    const meterValue = settings?.meter_value;
    const meterStamp = settings?.meter_last_update_at;

    useEffect(() => {
        if (meterValue == null) return;
        const level = getMochiMeter(meterValue).current.level;
        const prev = lastLevelRef.current;
        if (prev !== null && level < prev) {
            // Drop detected — single soft toast, low-key styling.
            toast(pickHungerDropPhrase(), {
                duration: 4500,
                // No sonner type (= default neutral) so it never reads as
                // a warning or error.
            });
        }
        lastLevelRef.current = level;
    }, [meterValue, meterStamp]);
}
